import { ReactElement, useState } from 'react';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown, faChevronUp, faTimes, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';

import { useLayout } from 'lib/common/contexts/layout/LayoutContext';

import ClickableIcon from 'lib/common/components/ClickableIcon';
import Icon from 'lib/common/components/Icon';

import { IconSizeConstraints } from '../Icon/Icon';

import styles from './warning-banner.module.scss';

type TWarningBannerProps = {
  title?: ReactElement;
  body: ReactElement;
  icon?: IconDefinition;
  className?: string;
  onClose?: () => void;
  'data-testid'?: string;
};

export default function WarningBanner({ title, body, icon, className, onClose, ...props }: TWarningBannerProps) {
  const [open, setOpen] = useState(true);
  const { isSoftphone } = useLayout();

  return (
    <div
      role="alert"
      className={cx(styles['warning-banner'], className)}
      data-testid={props['data-testid'] || 'warning-banner'}
    >
      <div
        className={cx(styles['warning-banner__content'], {
          [styles['warning-banner__content--softphone']]: isSoftphone,
          [styles['warning-banner__content--open']]: open,
        })}
      >
        <div
          className={cx(styles['warning-banner__content__titlebar'], {
            [styles['warning-banner__content__titlebar--softphone']]: isSoftphone,
          })}
        >
          <Icon icon={icon || faTriangleExclamation} size={15} color="warning" />
          <div className="full-width">
            {title}
            <div
              className={cx(styles['warning-banner__content__body'], {
                [styles['warning-banner__content__body--hidden']]: !open,
              })}
            >
              {body}
            </div>
          </div>
          {onClose ? (
            <ClickableIcon
              aria-label={`Dismiss banner`}
              icon={faTimes}
              size={15}
              onlyConstrain={IconSizeConstraints.HEIGHT}
              onClick={onClose}
            />
          ) : (
            <ClickableIcon
              aria-label={`${open ? 'Shrink' : 'Expand'} banner`}
              icon={open ? faChevronUp : faChevronDown}
              size={13}
              onClick={() => setOpen((state) => !state)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
